import { Drawer } from 'antd';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  useAccount,
  useContact,
  useDigitalQuote,
  useSupplierFacilitiesList,
} from '@frontend/api';

import Spinner from 'components/Spinner/Spinner';

import { GenericError } from 'modules/common/components';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import {
  getContact,
  getIsQuoteReadOnly,
  getQuote,
} from 'store/quotes/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';

import {
  facilitateOrderDrawer,
  receiveFacilitateOrderDrawerData,
} from '../data/actions';
import {
  getQuoteItems,
  getTab,
  isFacilitateOrderDrawerOpen,
} from '../data/reducer';
import CustomerInformation from './components/CustomerInformation';
import PartSelection from './components/PartSelection';
import PartSelectionFooter from './components/PartSelectionFooter';
import ShippingAndPaymentFooter from './components/ShippingAndPaymentFooter';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 13px;
  overflow: auto;
  width: 100%;
`;

const TopContainer = styled.div`
  border-bottom: 1px solid #eeeeee;
`;

const Label = styled.div`
  color: #000000;
  font-size: 14px;
`;

const OptionalText = styled.span`
  color: #bababa;
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding-bottom: 0px;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const Title = styled.div`
  color: #000000;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const LoadingContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const useInitFacilitateOrderDrawer = ({ isOpen }: { isOpen: boolean }) => {
  const dispatch = useDispatch();
  const quote = useSelector(getQuote);
  const quoteContact = useSelector(getContact);
  const isQuoteReadOnly = useSelector(getIsQuoteReadOnly);
  const maxSupplierFacilities = useFeatureFlagValue(
    featureFlagKeys.maxSupplierFacilities,
  );

  const supplierFacilityId = quote?.supplierFacility?.id;
  const digiQuoteKey = quote?.digiQuoteKey;

  const { data: contact } = useContact(
    { contactId: quoteContact?.id },
    { queryOptions: { enabled: isOpen } },
  );
  const { data: account } = useAccount(
    { accountId: contact?.account?.id },
    { queryOptions: { enabled: !!contact?.account && isOpen } },
  );
  const { data: supplierFacilities } = useSupplierFacilitiesList({
    queryOptions: { enabled: isOpen },
  });
  const { data: quoteResponse } = useDigitalQuote(
    { digiQuoteKey: digiQuoteKey ?? '', isPreview: false },
    { queryOptions: { enabled: isOpen && !!digiQuoteKey } },
  );
  const quoteItems = useMemo(() => {
    if (quoteResponse?.quoteItems) {
      return quoteResponse.quoteItems.map((quoteItem) => ({
        ...quoteItem,
        uuid: nanoid(),
      }));
    }
    return [];
  }, [quoteResponse]);
  // Only dispatch when all data is loaded and drawer is open
  useEffect(() => {
    if (
      isOpen &&
      contact &&
      (account || !contact.account) &&
      supplierFacilities &&
      quoteItems &&
      supplierFacilityId
    ) {
      dispatch(
        receiveFacilitateOrderDrawerData({
          account,
          contact,
          isQuoteReadOnly,
          maxSupplierFacilities,
          quoteItems,
          supplierFacilities,
          supplierFacilityId,
        }),
      );
    }
  }, [
    isOpen,
    contact,
    account,
    supplierFacilities,
    quoteItems,
    supplierFacilityId,
    dispatch,
    isQuoteReadOnly,
    maxSupplierFacilities,
  ]);
};

const FacilitateOrderDrawer = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isFacilitateOrderDrawerOpen);
  const quoteItems = useSelector(getQuoteItems);
  const tab = useSelector(getTab);
  const close = useCallback(() => {
    dispatch(facilitateOrderDrawer.close());
  }, [dispatch]);

  const quote = useSelector(getQuote);
  // Initialize drawer data when opened
  useInitFacilitateOrderDrawer({ isOpen });

  return (
    <>
      <StyledDrawer
        destroyOnClose={true}
        footer={
          tab === 'part_selection' ? (
            <PartSelectionFooter />
          ) : (
            <ShippingAndPaymentFooter />
          )
        }
        maskClosable={false}
        onClose={close}
        placement="right"
        visible={isOpen}
        width="500px"
      >
        <ErrorBoundary
          fallback={
            <Wrapper>
              <GenericError size="lg" height="100%" justifyContent="center" />
            </Wrapper>
          }
          onError={(error) =>
            console.error('Error occurred in FacilitateOrderDrawer', error)
          }
        >
          {quoteItems.length > 0 && quote ? (
            <Wrapper>
              <TopContainer>
                <Label>FACILITATE ORDER</Label>
                <Title>
                  {tab === 'part_selection' ? (
                    'Part Selection'
                  ) : (
                    <>
                      Shipping & Payment <OptionalText>(Optional)</OptionalText>
                    </>
                  )}
                </Title>
              </TopContainer>
              <Content>
                {tab === 'part_selection' ? (
                  <PartSelection quote={quote} />
                ) : (
                  <CustomerInformation quote={quote} />
                )}
              </Content>
            </Wrapper>
          ) : (
            <LoadingContainer>
              <Spinner large />
            </LoadingContainer>
          )}
        </ErrorBoundary>
      </StyledDrawer>
    </>
  );
};

export default FacilitateOrderDrawer;
